const uuidPattern =
  /[a-z0-9]{8}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{12}/

export function buildGraphitePath(
  location: {
    readonly hash: string
    readonly pathname: string
    readonly hostname: string
  },
  category: string,
  name: string
) {
  const subdomain = location.hostname.split('.')[0]
  const portalType =
    location.pathname.indexOf('admin_index.html') > -1 ? 'admin' : 'org'
  const portalID =
    portalType === 'admin'
      ? 'admin-id'
      : location.hash.split('/').length > 1 &&
        uuidPattern.test(location.hash.split('/')[1])
      ? location.hash.split('/')[1]
      : 'public'
  return `${subdomain}.${portalType}.${portalID}.ui.${category}.${name}.${location.hash}`
}

export class Analytics {
  public static sendPageView(fields: {
    readonly page: string
    readonly title: string
  }) {
    Analytics.invoke('set', fields)
    Analytics.invoke('send', 'pageview')
  }

  public static sendEvent(fields: {
    readonly category: string
    readonly action: string
    readonly label: string
  }) {
    Analytics.invoke('send', 'event', fields)
  }

  public static sendException(fields: {
    readonly exDescription: string
    readonly exFatal: boolean
  }) {
    Analytics.invoke('send', 'exception', fields)
  }

  public static sendTiming(category: string, name: string, value: number) {
    Analytics.invoke('send', 'timing', category, name, Math.round(value))
  }

  private static invoke(command: string, ...fields: any[]) {
    if (typeof ga !== 'undefined') {
      ga(command, ...fields)
    }
  }
}

export class Performance {
  /**
   * Mark the occurence of an event
   *
   * @param name - event name
   */
  public static mark(name: string) {
    if (window && window.performance && window.performance.mark) {
      window.performance.mark(name)
    }
  }

  /**
   * Measure the timing of an event
   *
   * @param data
   *    @param category - grouping for measurements
   *    @param name - measurement name
   *    @param startMark - optional name of previously marked event. If provided, the
   *                       measurement will be from this mark until 'now'. Otherwise the
   *                       measurement is taken from the time of page load.
   * @param options
   *    @param limit - default to false. If true, this measurement will only be taken
   *                   once per browser session
   */
  public static measure(
    data: {
      readonly category: string
      readonly name: string
      readonly startMark?: string
    },
    options: { readonly limit?: boolean } = {}
  ) {
    const { category, name, startMark } = data
    const { limit = false } = options

    if (
      window &&
      window.performance &&
      typeof window.performance.measure === 'function' &&
      (!limit || window.performance.getEntriesByName(name).length === 0) &&
      (!startMark || window.performance.getEntriesByName(startMark).length > 0)
    ) {
      if (!startMark) {
        window.performance.mark(name)
      } else {
        window.performance.measure(name, startMark)
      }
      Performance.send(category, name)
    }
  }

  private static send(category: string, name: string) {
    const entries = window.performance.getEntriesByName(name)
    if (entries.length > 0) {
      const entry = entries[entries.length - 1]
      Analytics.sendTiming(category, name, entry.duration)
    }
  }
}
